import React, { useState, useEffect, useCallback } from "react";

const BALLOON_COLORS = [
  "#DC283C", // DoozyGo Red
  "#F0DC00", // DoozyGo Gold
  "#E83E8C", // Pink
  "#FF9F1C", // Orange
  "#7C3AED", // Purple
  "#10B981", // Green
  "#3B82F6", // Blue
  "#F97316", // Bright Orange
];

const createBalloon = (id) => {
  const color = BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)];
  const x = Math.random() * 100; // % from left
  const size = 30 + Math.random() * 25; // 30-55px
  const delay = Math.random() * 2; // 0-2s delay
  const duration = 2.5 + Math.random() * 2; // 2.5-4.5s float time
  const wobble = -15 + Math.random() * 30; // horizontal drift
  return { id, color, x, size, delay, duration, wobble };
};

const playSuccessSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    // Cheerful ascending chime
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.12 + 0.4);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.4);
    });

    // Final sparkle
    setTimeout(() => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "triangle";
      osc.frequency.value = 1568; // G6
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
    }, 500);
  } catch {
    // Silent fallback
  }
};

const BalloonCelebration = ({ show, onComplete }) => {
  const [balloons, setBalloons] = useState([]);
  const [confetti, setConfetti] = useState([]);
  const [message, setMessage] = useState(false);

  const startCelebration = useCallback(() => {
    // Create balloons in waves
    const wave1 = Array.from({ length: 15 }, (_, i) => createBalloon(i));
    const wave2 = Array.from({ length: 10 }, (_, i) => createBalloon(i + 15));

    setBalloons(wave1);
    setMessage(true);
    playSuccessSound();

    // Second wave
    setTimeout(() => setBalloons(prev => [...prev, ...wave2]), 800);

    // Create confetti
    const confettiPieces = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)],
      delay: Math.random() * 1.5,
      size: 4 + Math.random() * 6,
      rotation: Math.random() * 360,
    }));
    setConfetti(confettiPieces);

    // Cleanup after animation
    setTimeout(() => {
      setBalloons([]);
      setConfetti([]);
      setMessage(false);
      if (onComplete) onComplete();
    }, 5000);
  }, [onComplete]);

  useEffect(() => {
    if (show) startCelebration();
  }, [show, startCelebration]);

  if (!show && balloons.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-testid="balloon-celebration">
      <style>{`
        @keyframes balloonRise {
          0% { transform: translateY(100vh) translateX(0px) scale(0.3); opacity: 0; }
          10% { opacity: 1; transform: translateY(80vh) translateX(var(--wobble)) scale(0.8); }
          50% { opacity: 1; transform: translateY(40vh) translateX(calc(var(--wobble) * -0.5)) scale(1); }
          90% { opacity: 0.8; transform: translateY(5vh) translateX(var(--wobble)) scale(1.05); }
          100% { opacity: 0; transform: translateY(-10vh) translateX(0px) scale(0.9); }
        }
        @keyframes confettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes successPop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes successFade {
          0%, 70% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>

      {/* Balloons rising from bottom */}
      {balloons.map(b => (
        <div key={b.id}
          style={{
            position: "absolute",
            left: `${b.x}%`,
            bottom: 0,
            "--wobble": `${b.wobble}px`,
            animation: `balloonRise ${b.duration}s ease-out ${b.delay}s forwards`,
            opacity: 0,
          }}>
          {/* Balloon SVG */}
          <svg width={b.size} height={b.size * 1.3} viewBox="0 0 40 52">
            <defs>
              <radialGradient id={`bg${b.id}`} cx="35%" cy="30%">
                <stop offset="0%" stopColor="white" stopOpacity="0.3" />
                <stop offset="100%" stopColor={b.color} />
              </radialGradient>
            </defs>
            <ellipse cx="20" cy="18" rx="17" ry="20" fill={`url(#bg${b.id})`} />
            <polygon points="17,37 23,37 20,43" fill={b.color} opacity="0.8" />
            <line x1="20" y1="43" x2="20" y2="52" stroke={b.color} strokeWidth="0.8" opacity="0.5" />
          </svg>
        </div>
      ))}

      {/* Confetti */}
      {confetti.map(c => (
        <div key={`c${c.id}`}
          style={{
            position: "absolute",
            left: `${c.x}%`,
            top: 0,
            width: c.size,
            height: c.size,
            backgroundColor: c.color,
            borderRadius: c.id % 3 === 0 ? "50%" : "2px",
            animation: `confettiFall ${2 + Math.random() * 2}s linear ${c.delay}s forwards`,
            transform: `rotate(${c.rotation}deg)`,
            opacity: 0,
          }}
        />
      ))}

      {/* Success Message */}
      {message && (
        <div className="absolute inset-0 flex items-center justify-center"
          style={{ animation: "successFade 4.5s ease-out forwards" }}>
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl px-8 py-5 shadow-2xl border-2 border-[#F0DC00] text-center pointer-events-auto"
            style={{ animation: "successPop 0.5s ease-out forwards" }}>
            <p className="text-3xl mb-1">🎉</p>
            <p className="text-lg font-bold text-[#1E1B4B]">Booking Confirmed!</p>
            <p className="text-sm text-[#DC283C] font-medium mt-1">Let's get your party started</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalloonCelebration;
